import { safeGetSession } from './supabase';
import { getAccessToken } from './authStore';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

export type DeleteAccountReason =
  | 'too_expensive'
  | 'missing_features'
  | 'too_complex'
  | 'switched_tool'
  | 'not_using'
  | 'other';

export type DeleteAccountPayload = {
  reason?: DeleteAccountReason | '';
  comment?: string;
};

async function getJwtOrThrow(): Promise<string> {
  const token =
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('AUTH_REQUIRED');
  }
  return token;
}

export const accountService = {
  async deleteAccount(payload: DeleteAccountPayload = {}): Promise<void> {
    if (!apiBaseUrl) {
      throw new Error('Missing REACT_APP_API_BASE_URL');
    }

    const token = await getJwtOrThrow();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    let res: Response;
    try {
      res = await fetch(`${apiBaseUrl}/account/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: payload.reason || undefined,
          comment: payload.comment?.trim() || undefined,
        }),
        signal: controller.signal,
      });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        throw new Error('DELETE_ACCOUNT_TIMEOUT');
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }

    if (!res.ok) {
      const bodyText = await res.text().catch(() => '');
      try {
        const json = JSON.parse(bodyText);
        if (json?.code) throw new Error(String(json.code));
        if (json?.message) throw new Error(String(json.message));
      } catch (parseError) {
        if (parseError instanceof Error && !parseError.message.includes('JSON')) {
          throw parseError;
        }
      }
      throw new Error(`DELETE_ACCOUNT_${res.status}${bodyText ? `: ${bodyText}` : ''}`);
    }
  },
};
